import { Alert, Box, Heading, Icon, Switch, Text, Tooltip } from '@ksyos/design-system';
import type { LegendItem } from '@frontend/components/elements/chart/ChartLegend';
import { useVanDerLaarDistributionFromDate } from '@common/constants';
import { concatOrNull } from '@common/utils/textUtils';
import { ChartLegend } from '@frontend/components/elements/chart/ChartLegend';

const defaultLegendItems: LegendItem[] = [
    {
        icon: 'circle-magenta',
        label: 'Meting door patiënt',
    },
    {
        icon: 'triangle-blue',
        label: 'Meting door OCA',
    },
];

const legendItemsOcaDistribution: LegendItem[] = [
    ...defaultLegendItems,
    {
        icon: 'dotted-line',
        label: 'Mediaan van populatie',
    },
    {
        icon: 'solid',
        label: 'Bandbreedte gewicht',
    },
];

const legendItemsVanDerLaarDistribution: LegendItem[] = [
    ...defaultLegendItems,
    {
        icon: 'dotted-line',
        label: 'Gemiddelde van populatie',
    },
    {
        icon: 'solid',
        label: '+1 en -1 standaarddeviatie',
    },
    {
        icon: 'solid-gray',
        label: '+2 en -2 standaarddeviatie',
    },
];

const tooltipContentOcaDistribution =
    "Total Weight Loss (TWL) geeft het percentage aan van het verloren gewicht na de ingreep. 'Mediaan van populatie' in de legenda betekent dat de lijn de mediaan van het gewichtsverlies is voor patiënten met dezelfde ingreep en geslacht als de patiënt.";

const tooltipContentVanDerLaarDistribution =
    "Total Weight Loss (TWL) geeft het percentage van het verloren gewicht aan na de ingreep. 'Gemiddelde van populatie' in de legenda betekent dat de lijn het gemiddelde van het gewichtsverlies is van patiënten met dezelfde ingreep als de patiënt.";

type MonitoringChartHeaderProps = {
    showOcaDistribution: boolean;
    showToggleForOcaDistribution: boolean;
    titleWarningText?: string;
    toggleIsChecked?: boolean;
    toggleShowOcaDistribution?: () => void;
    warningText?: string;
    weightBeforeSurgery?: number | null;
};

export function TotalWeightLossMonitoringChartHeader(props: MonitoringChartHeaderProps) {
    const {
        showOcaDistribution,
        showToggleForOcaDistribution,
        titleWarningText,
        toggleShowOcaDistribution,
        warningText,
        weightBeforeSurgery,
    } = props;

    const legendItems = showOcaDistribution ? legendItemsOcaDistribution : legendItemsVanDerLaarDistribution;

    return (
        <Box
            display="flex"
            flexDirection={['column', null, 'row']}
            gap={[2, null, 8]}
            justifyContent="space-between"
            mb={[6]}
        >
            <Box mb={[4, null, 0]}>
                <Box
                    alignItems="center"
                    display="flex"
                    flex-direction="column"
                    flexWrap={['wrap', 'nowrap']}
                    gap={6}
                    mb={titleWarningText ? 0 : 2}
                >
                    <Heading
                        as="h3"
                        fontWeight="medium"
                        size="medium"
                        sx={{
                            flex: [1, 'initial'],
                        }}
                    >
                        Percentage gewichtsverlies (%TWL)
                    </Heading>

                    <Tooltip
                        content={
                            showOcaDistribution ? tooltipContentOcaDistribution : tooltipContentVanDerLaarDistribution
                        }
                    >
                        <Icon
                            color="primary"
                            icon="information"
                        />
                    </Tooltip>

                    {titleWarningText && (
                        <Alert
                            appearance="warning"
                            hasIcon={true}
                            noMargin={true}
                        >
                            <Alert.Text
                                fontWeight="bold"
                                whiteSpace="nowrap"
                            >
                                {titleWarningText}
                            </Alert.Text>
                        </Alert>
                    )}
                </Box>

                {props.weightBeforeSurgery && (
                    <Text mt={titleWarningText ? 0 : 2}>{`Startgewicht: ${concatOrNull(
                        weightBeforeSurgery,
                        'kg'
                    )}`}</Text>
                )}

                {showToggleForOcaDistribution && (
                    <Box sx={{ display: 'flex', mt: 6 }}>
                        <Text>Zie bandbreedte tot en met {useVanDerLaarDistributionFromDate.format('DD-MM-YYYY')}</Text>
                        <Switch
                            checked={showOcaDistribution}
                            onChange={toggleShowOcaDistribution}
                            size="medium"
                            sx={{ ml: 4 }}
                        />
                    </Box>
                )}
            </Box>
            {!warningText && <ChartLegend legendItems={legendItems} />}
        </Box>
    );
}
